/*  MediaLibraryTab — Browse, upload, tag, delete every marketing asset.
    ────────────────────────────────────────────────────────────────
    iter100.150 — Central asset browser. Every image uploaded via
    the new /admin/media/upload endpoint or the older hero uploader
    surfaces here so admins can preview, copy URL, edit alt/tags,
    and delete.

    Endpoints:
        GET    /api/admin/media
        POST   /api/admin/media/upload
        PATCH  /api/admin/media/{id}
        DELETE /api/admin/media/{id}
*/
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
    UploadSimple, Trash, Copy, ArrowsClockwise, ImageSquare,
    Tag as TagIcon, PencilSimple, Check,
} from "@phosphor-icons/react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";


const CATEGORY_OPTIONS = [
    { value: "all",         label: "All assets" },
    { value: "hero",        label: "Hero images" },
    { value: "testimonial", label: "Testimonial portraits" },
    { value: "general",     label: "General" },
    { value: "logo",        label: "Logos & brand" },
];


export default function MediaLibraryTab() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [filterCat, setFilterCat] = useState("all");
    const [editingId, setEditingId] = useState(null);
    const [editAlt, setEditAlt] = useState("");
    const [editTags, setEditTags] = useState("");
    const fileInputRef = useRef(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = filterCat && filterCat !== "all" ? { category: filterCat } : {};
            const { data } = await api.get("/admin/media", { params });
            setItems(data.items || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't load media.");
        } finally { setLoading(false); }
    }, [filterCat]);

    useEffect(() => { load(); }, [load]);

    const catCounts = useMemo(() => {
        const c = {};
        for (const it of items) c[it.category] = (c[it.category] || 0) + 1;
        c.all = items.length;
        return c;
    }, [items]);

    const upload = async (fileList) => {
        const files = Array.from(fileList || []);
        if (files.length === 0) return;
        setUploading(true);
        let ok = 0;
        for (const f of files) {
            try {
                const fd = new FormData();
                fd.append("file", f);
                fd.append("category", filterCat !== "all" ? filterCat : "general");
                fd.append("alt", "");
                await api.post("/admin/media/upload", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                ok++;
            } catch (e) {
                toast.error(`${f.name} · ${e?.response?.data?.detail || "upload failed"}`);
            }
        }
        setUploading(false);
        if (ok > 0) {
            toast.success(`Uploaded ${ok} asset${ok === 1 ? "" : "s"}.`);
            load();
        }
    };

    const remove = async (it) => {
        if (!window.confirm(`Delete "${it.original_filename}"? Any page still using this URL will show a broken image.`)) return;
        try {
            await api.delete(`/admin/media/${it.id}`);
            toast.success("Deleted.");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't delete.");
        }
    };

    const copyUrl = async (it) => {
        try {
            await navigator.clipboard.writeText(it.url);
            toast.success("URL copied to clipboard.");
        } catch {
            // Fallback: show a prompt-style modal in future; toast for now.
            toast.info(it.url);
        }
    };

    const beginEdit = (it) => {
        setEditingId(it.id);
        setEditAlt(it.alt || "");
        setEditTags((it.tags || []).join(", "));
    };

    const saveEdit = async (it) => {
        try {
            await api.patch(`/admin/media/${it.id}`, {
                alt: editAlt,
                tags: editTags.split(",").map((t) => t.trim()).filter(Boolean),
            });
            toast.success("Metadata saved.");
            setEditingId(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't save.");
        }
    };

    const bytesLabel = (b) => {
        if (!b) return "";
        if (b < 1024) return `${b}B`;
        if (b < 1024 * 1024) return `${Math.round(b / 1024)}KB`;
        return `${(b / (1024 * 1024)).toFixed(1)}MB`;
    };

    return (
        <div className="space-y-4" data-testid="media-library-tab">
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary">Media Library</div>
                    <h2 className="font-display text-lg font-bold text-foreground mt-0.5">Every image on the marketing site</h2>
                    <div className="text-xs text-muted-foreground mt-0.5">
                        Upload once, reuse anywhere. Click any thumbnail to copy its URL, edit alt text, or delete.
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="media-refresh">
                        <ArrowsClockwise size={11} weight="bold" className="mr-1" /> Refresh
                    </Button>
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => { upload(e.target.files); e.target.value = ""; }}
                        data-testid="media-upload-input"
                    />
                    <Button size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="media-upload-trigger">
                        <UploadSimple size={11} weight="bold" className="mr-1" />
                        {uploading ? "Uploading…" : "Upload"}
                    </Button>
                </div>
            </div>

            {/* Category filter */}
            <div className="flex flex-wrap gap-2" data-testid="media-filter">
                {CATEGORY_OPTIONS.map((c) => (
                    <button
                        key={c.value}
                        type="button"
                        onClick={() => setFilterCat(c.value)}
                        className={`px-3 py-1.5 text-xs font-semibold rounded-full border transition-colors ${
                            filterCat === c.value ? "bg-primary border-primary text-white" : "bg-card border-border text-muted-foreground hover:bg-secondary"
                        }`}
                        data-testid={`media-filter-${c.value}`}
                    >
                        {c.label} {catCounts[c.value] != null ? `(${catCounts[c.value]})` : ""}
                    </button>
                ))}
            </div>

            {/* Drop zone / grid */}
            {loading ? (
                <div className="text-sm text-muted-foreground py-8 text-center">Loading…</div>
            ) : items.length === 0 ? (
                <div
                    className="border-2 border-dashed border-border rounded-2xl py-12 text-center bg-secondary/20 cursor-pointer hover:border-primary/40"
                    onClick={() => fileInputRef.current?.click()}
                    onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files); }}
                    onDragOver={(e) => e.preventDefault()}
                    data-testid="media-empty"
                >
                    <UploadSimple size={28} className="mx-auto text-muted-foreground/60" />
                    <div className="mt-2 text-sm font-semibold text-foreground">Drop files here or click to upload</div>
                    <div className="text-xs text-muted-foreground mt-1">JPEG, PNG, WebP, GIF · up to 6 MB each</div>
                </div>
            ) : (
                <div
                    className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3"
                    onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files); }}
                    onDragOver={(e) => e.preventDefault()}
                    data-testid="media-grid"
                >
                    {items.map((it) => {
                        const isEditing = editingId === it.id;
                        return (
                            <div key={it.id} className="bg-card border border-border rounded-xl overflow-hidden group hover:border-primary/40 transition-colors" data-testid={`media-item-${it.id}`}>
                                <div className="aspect-square bg-secondary/40 relative overflow-hidden">
                                    <img src={it.url} alt={it.alt || it.original_filename} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/45 transition-colors flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100">
                                        <button onClick={() => copyUrl(it)} className="w-8 h-8 grid place-items-center rounded-full bg-white text-foreground hover:text-primary" title="Copy URL" data-testid={`media-copy-${it.id}`}>
                                            <Copy size={12} weight="bold" />
                                        </button>
                                        <button onClick={() => beginEdit(it)} className="w-8 h-8 grid place-items-center rounded-full bg-white text-foreground hover:text-primary" title="Edit alt/tags" data-testid={`media-edit-${it.id}`}>
                                            <PencilSimple size={12} weight="bold" />
                                        </button>
                                        <button onClick={() => remove(it)} className="w-8 h-8 grid place-items-center rounded-full bg-white text-foreground hover:text-rose-600" title="Delete" data-testid={`media-delete-${it.id}`}>
                                            <Trash size={12} weight="bold" />
                                        </button>
                                    </div>
                                    {it.category && it.category !== "general" && (
                                        <div className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded bg-white/95 backdrop-blur text-[9px] font-bold uppercase tracking-wider text-foreground">
                                            {it.category}
                                        </div>
                                    )}
                                </div>
                                <div className="p-2 space-y-1">
                                    <div className="text-[10.5px] font-medium text-foreground truncate" title={it.original_filename}>{it.original_filename}</div>
                                    <div className="text-[9.5px] text-muted-foreground flex items-center justify-between">
                                        <span>{bytesLabel(it.size)}</span>
                                        {it.tags?.length > 0 && (
                                            <span className="inline-flex items-center gap-0.5" title={it.tags.join(", ")}>
                                                <TagIcon size={9} weight="fill" className="text-primary" />
                                                {it.tags.length}
                                            </span>
                                        )}
                                    </div>
                                    {isEditing && (
                                        <div className="space-y-1 pt-1 border-t border-border/60">
                                            <Input value={editAlt} onChange={(e) => setEditAlt(e.target.value)} placeholder="Alt text" className="text-[10px] h-7" data-testid={`media-edit-alt-${it.id}`} />
                                            <Input value={editTags} onChange={(e) => setEditTags(e.target.value)} placeholder="tags, comma, separated" className="text-[10px] h-7" data-testid={`media-edit-tags-${it.id}`} />
                                            <div className="flex gap-1">
                                                <button onClick={() => saveEdit(it)} className="flex-1 h-6 rounded bg-primary text-white text-[10px] font-semibold" data-testid={`media-save-${it.id}`}>
                                                    <Check size={9} weight="bold" className="inline mr-0.5" /> Save
                                                </button>
                                                <button onClick={() => setEditingId(null)} className="h-6 px-2 rounded border border-border text-[10px] text-muted-foreground">Cancel</button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Persistent uploader footer when populated */}
            {items.length > 0 && (
                <div
                    className="border-2 border-dashed border-border rounded-xl py-6 text-center bg-secondary/10 cursor-pointer hover:border-primary/40 transition-colors"
                    onClick={() => fileInputRef.current?.click()}
                    onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files); }}
                    onDragOver={(e) => e.preventDefault()}
                    data-testid="media-drop-zone"
                >
                    <UploadSimple size={20} className="mx-auto text-muted-foreground/60" />
                    <div className="mt-1.5 text-xs font-semibold text-foreground">Drop files anywhere on the grid or click to upload</div>
                    <div className="text-[10.5px] text-muted-foreground mt-0.5">Multiple files supported · JPEG/PNG/WebP/GIF · 6 MB each</div>
                </div>
            )}
        </div>
    );
}
